<template>
  <teleport to="body">
    <transition name="popup-fade">
      <div v-if="visible" class="popup-overlay" @click.self="handleBackdropClick">
        <div class="popup-container" :class="`popup-${type}`">
          <div class="popup-content">
            <!-- Icon with colored background -->
            <div class="popup-icon-wrapper" :class="`icon-${type}`">
              <i :class="iconClass"></i>
            </div>

            <!-- Title -->
            <h3 v-if="title" class="popup-title">{{ title }}</h3>

            <!-- Message -->
            <p class="popup-message">{{ message }}</p>

            <!-- Action Button -->
            <button 
              class="popup-button" 
              :class="`button-${type}`"
              @click="handleConfirm"
            >
              {{ buttonText }}
            </button>
          </div>
        </div>
      </div>
    </transition>
  </teleport>
</template>

<script setup>
import { computed } from 'vue';

const props = defineProps({
  visible: {
    type: Boolean,
    default: false
  },
  type: {
    type: String,
    default: 'info',
    validator: (value) => ['info', 'success', 'warning', 'error'].includes(value)
  },
  title: {
    type: String,
    default: ''
  },
  message: {
    type: String,
    required: true
  },
  buttonText: {
    type: String,
    default: 'OK'
  },
  allowBackdropClose: {
    type: Boolean,
    default: true
  }
});

const emit = defineEmits(['confirm', 'close']);

const iconClass = computed(() => {
  const icons = {
    info: 'ph ph-info',
    success: 'ph ph-check-circle',
    warning: 'ph ph-warning',
    error: 'ph ph-x-circle'
  };
  return icons[props.type] || icons.info;
});

const handleConfirm = () => {
  emit('confirm');
  emit('close');
};

const handleBackdropClick = () => {
  if (props.allowBackdropClose) {
    emit('close');
  }
};
</script>

<style scoped>
.popup-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: 20px;
}

.popup-container {
  background: var(--color-bg-card);
  border-radius: 16px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  max-width: 400px;
  width: 100%;
  overflow: hidden;
  animation: popup-scale 0.3s ease-out;
}

@keyframes popup-scale {
  from {
    opacity: 0;
    transform: scale(0.9);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

.popup-content {
  padding: 32px 24px 24px;
  text-align: center;
}

.popup-icon-wrapper {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
  font-size: 32px;
}

.icon-info {
  background: var(--color-brand-subtle);
  color: var(--color-brand);
}

.icon-success {
  background: rgba(45, 205, 178, 0.1);
  color: var(--bs-success, #2dcdb2);
}

.icon-warning {
  background: rgba(255, 186, 104, 0.1);
  color: var(--bs-warning, #ffba68);
}

.icon-error {
  background: rgba(255, 155, 138, 0.1);
  color: var(--bs-danger, #ff9b8a);
}

.popup-title {
  font-size: 20px;
  font-weight: 600;
  color: var(--color-text);
  margin: 0 0 12px;
}

.popup-message {
  font-size: 15px;
  color: var(--color-text-muted);
  line-height: 1.6;
  margin: 0 0 24px;
  word-wrap: break-word;
}

.popup-button {
  width: 100%;
  padding: 12px 24px;
  border: none;
  border-radius: 1.5rem;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  outline: none;
}

.button-info {
  background: var(--color-brand);
  color: white;
}

.button-info:hover {
  background: var(--color-brand-hover);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(var(--color-brand-rgb), 0.3);
}

.button-success {
  background: var(--bs-success, #2dcdb2);
  color: white;
}

.button-success:hover {
  background: var(--bs-success-hover-bg, var(--teal-500));
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(45, 205, 178, 0.3);
}

.button-warning {
  background: var(--bs-warning, #ffba68);
  color: white;
}

.button-warning:hover {
  background: var(--bs-warning-hover-bg, #ffa845);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(255, 186, 104, 0.3);
}

.button-error {
  background: var(--bs-danger, #ff9b8a);
  color: white;
}

.button-error:hover {
  background: var(--bs-danger-hover-bg, #ff7a65);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(255, 155, 138, 0.3);
}

.popup-button:active {
  transform: translateY(0);
}

/* Animations */
.popup-fade-enter-active,
.popup-fade-leave-active {
  transition: opacity 0.3s ease;
}

.popup-fade-enter-from,
.popup-fade-leave-to {
  opacity: 0;
}

/* Responsive */
@media (max-width: 576px) {
  .popup-container {
    max-width: 90%;
    margin: 0 auto;
  }

  .popup-content {
    padding: 24px 20px 20px;
  }

  .popup-icon-wrapper {
    width: 56px;
    height: 56px;
    font-size: 28px;
    margin-bottom: 16px;
  }

  .popup-title {
    font-size: 18px;
  }

  .popup-message {
    font-size: 14px;
  }
}
</style>

